import { FC, memo, useMemo } from 'react'
import Script from 'next/script'
import { PageProps, ResponseLocals } from '../common/types'
import { GetServerSideProps } from '../redux/types'
import { getLocaleFromLanguage, getMessagesFromLanguage } from '../i18n/utils'
import { deleteSession } from '../utils/delete-session'
import { getRoutesFromDomain } from '../routes/utils'
import { BaseTransport } from '../transports'
import { useUserDetails } from '../hooks/users'
import Page from '../components/common/page'
import HomePage from '../components/home'
import { pageClient } from '../clients'
import HomePageTransport from '../transports/home-page-transport'
import { useTranslation } from '../hooks/locale'
import { getOrganizationSchemaDetail, getFAQSchemaHTML } from '../schema/common'
import { getWebsiteSchemaHTML } from '../schema/home-page'
import { useSetWishlistCount } from '../hooks/wishlist'
import { generateHeroImagePreloadLinks } from '../components/common/hero-section/utils'
import { HOME_BACKGROUND_IMAGES } from '../components/common/hero-section/constants'
import { getFilterFromRequest } from '../utils/request-utils'

type Props = {
    fullUrl: string
    locale: string
    data: BaseTransport<HomePageTransport>
} & PageProps

const PRECONNECT_DOMAINS = ['https://storage.googleapis.com']

const HomePageContainer: FC<Props> = ({ fullUrl, data }) => {
    const homeData = data?.data
    const { userDetails } = useUserDetails({ additionalDetails: true })
    const isLoggedIn = !!userDetails?.id
    const { t } = useTranslation()
    const organizationSchema = useMemo(
        () => getOrganizationSchemaDetail(t, homeData?.topRatedObjects ?? undefined),
        [t, homeData?.topRatedObjects],
    )

    const websiteSchema = useMemo(() => getWebsiteSchemaHTML(t), [t])
    const faqSchema = useMemo(
        () => getFAQSchemaHTML(homeData?.staticData?.faqSection?.questionAnswers),
        [homeData?.staticData?.faqSection?.questionAnswers],
    )

    const staticHead = homeData?.staticData?.head
    const heroImagePreloadLinks = useMemo(() => generateHeroImagePreloadLinks(HOME_BACKGROUND_IMAGES), [])
    useSetWishlistCount()

    if (!homeData) {
        return null
    }

    return (
        <Page
            head={homeData.head}
            pageHead={staticHead}
            imagePreloadLinks={heroImagePreloadLinks}
            preconnectDomains={PRECONNECT_DOMAINS}
        >
            {!!organizationSchema && (
                <Script
                    id="organization-schema"
                    type="application/ld+json"
                    dangerouslySetInnerHTML={organizationSchema}
                />
            )}
            {!!websiteSchema && (
                <Script id="website-schema" type="application/ld+json" dangerouslySetInnerHTML={websiteSchema} />
            )}
            {!!faqSchema && <Script id="faq-schema" type="application/ld+json" dangerouslySetInnerHTML={faqSchema} />}
            <HomePage userDetails={userDetails} isLoggedIn={isLoggedIn} url={fullUrl} homeDetails={homeData} />
        </Page>
    )
}

export const getServerSideProps: GetServerSideProps<Props> = async ({ req, res }) => {
    const { user, device } = res.locals as ResponseLocals
    const sitePathname = req.originalUrl
    const requestUserConfig = deleteSession(user)
    const protocol = req.header('x-forwarded-proto') || req.protocol || 'http'
    const host = req.header('host')
    const fullUrl = `${protocol}://${host}${sitePathname}`
    const locale = getLocaleFromLanguage(user.language)
    const filter = getFilterFromRequest(req)

    const data = await pageClient.getHomePage({ user, filter })
    if (data.status === 404) {
        return {
            notFound: true,
        }
    }

    return {
        props: {
            device,
            requestUserConfig: requestUserConfig,
            messages: await getMessagesFromLanguage(user.language),
            routes: await getRoutesFromDomain(user.domain),
            fullUrl,
            locale,
            data,
            originalPath: req.originalPath,
            originalQuery: req.originalQuery,
            originalUrl: req.originalUrl,
        },
    }
}

export default memo(HomePageContainer)
